/* eslint-disable react-refresh/only-export-components -- intentional: shared module co-exports helpers with components */
import React from 'react';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getAuthSession, registerLogoutCleanup } from '@/shared/api/client';

type ThemeMode = 'dark' | 'light';
type ButtonAppearance = 'primary' | 'secondary' | 'ghost';
type ButtonDimension = 's' | 'm';
type BadgeTone = 'neutral' | 'primary' | 'warning' | 'success' | 'danger';

const THEME_STORAGE_KEY = 'kaban.themeMode';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ThemeModeContext = React.createContext<{
  mode: ThemeMode;
  toggle: () => void;
}>({
  mode: 'dark',
  toggle: () => undefined,
});

const darkTheme = css`
  :root {
    color-scheme: dark;
    --bg-app: #0a0b10;
    --bg-primary: #12141c;
    --bg-surface: #191c26;
    --bg-sidebar: #0d1016;
    --bg-panel: rgba(17, 20, 28, 0.92);
    --bg-panel-muted: rgba(14, 17, 24, 0.96);
    --bg-panel-strong: rgba(20, 24, 34, 0.98);
    --bg-panel-elevated: #171b26;
    --bg-hover: rgba(255, 255, 255, 0.055);
    --bg-input: rgba(255, 255, 255, 0.045);
    --text-primary: #f5f7fb;
    --text-secondary: #9ca7bf;
    --text-tertiary: #5c6578;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --accent: #7c8cff;
    --accent-contrast: #0a0b10;
    --accent-border: rgba(124, 140, 255, 0.45);
    --accent-muted: rgba(124, 140, 255, 0.10);
    --accent-hover: #6b7bff;
    --badge-primary-bg: rgba(124, 140, 255, 0.14);
    --badge-warning-bg: rgba(255, 184, 77, 0.12);
    --badge-neutral-bg: rgba(255, 255, 255, 0.07);
    --shadow-lg: 0 24px 80px rgba(0, 0, 0, 0.42);
  }
`;

const lightTheme = css`
  :root {
    color-scheme: light;
    --bg-app: #f4f6fb;
    --bg-primary: #ffffff;
    --bg-surface: #f8f9fc;
    --bg-sidebar: rgba(255, 255, 255, 0.86);
    --bg-panel: rgba(255, 255, 255, 0.92);
    --bg-panel-muted: rgba(248, 250, 255, 0.96);
    --bg-panel-strong: rgba(255, 255, 255, 0.98);
    --bg-panel-elevated: #ffffff;
    --bg-hover: rgba(19, 27, 45, 0.05);
    --bg-input: rgba(19, 27, 45, 0.03);
    --text-primary: #121826;
    --text-secondary: #5f6b85;
    --text-tertiary: #8f96a3;
    --border-subtle: rgba(16, 24, 40, 0.1);
    --accent: #4f5dff;
    --accent-contrast: #ffffff;
    --accent-border: rgba(79, 93, 255, 0.3);
    --accent-muted: rgba(79, 93, 255, 0.07);
    --accent-hover: #3d4bff;
    --badge-primary-bg: rgba(79, 93, 255, 0.09);
    --badge-warning-bg: rgba(255, 184, 77, 0.16);
    --badge-neutral-bg: rgba(19, 27, 45, 0.06);
    --shadow-lg: 0 30px 80px rgba(30, 41, 59, 0.12);
  }
`;

const baseStyles = css`
  * {
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    margin: 0;
    min-height: 100%;
    font-family:
      Inter,
      ui-sans-serif,
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      sans-serif;
    background: var(--bg-app);
    color: var(--text-primary);
  }

  body {
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button,
  input,
  select,
  textarea {
    font: inherit;
  }

  :focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 4px;
  }

  ::selection {
    background: rgba(124, 140, 255, 0.25);
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--border-subtle);
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--text-secondary);
  }
`;

const ProvidersWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = React.useState<ThemeMode>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(THEME_STORAGE_KEY) : null;
    return stored === 'light' ? 'light' : 'dark';
  });

  const toggle = React.useCallback(() => {
    setMode((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  // Register queryClient.clear() as a logout cleanup handler once on mount.
  // This avoids circular imports between client.ts and basic.tsx.
  React.useEffect(() => {
    registerLogoutCleanup(() => queryClient.clear());
  }, []);

  // Cross-tab session sync: when another tab clears the session (logout) or
  // switches to a different user, force a hard reload so this tab starts fresh.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'kaban.session') return;
      if (e.newValue === null) {
        // Session was cleared in another tab — reload to land on login page.
        window.location.reload();
        return;
      }
      // Session changed in another tab — reload to start fresh on any user mismatch.
      // Treat missing/null userIds on either side as suspect: reloading on a foreign
      // value is cheap; missing a reload would be a security failure.
      if (e.oldValue === e.newValue) return;
      const currentSession = getAuthSession();
      try {
        const incomingSession = JSON.parse(e.newValue) as { userId?: number | null };
        const currentUserId = currentSession?.userId ?? null;
        const incomingUserId = incomingSession?.userId ?? null;
        if (currentUserId !== incomingUserId) {
          window.location.reload();
        }
      } catch {
        // Malformed value — reload defensively rather than silently ignore.
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <ThemeModeContext.Provider value={{ mode, toggle }}>
      <Global styles={[baseStyles, mode === 'dark' ? darkTheme : lightTheme]} />
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ThemeModeContext.Provider>
  );
};

export const Providers = ProvidersWrap;
export const AdmiralThemeProvider = ProvidersWrap;

export const useThemeMode = () => React.useContext(ThemeModeContext);

const SurfaceRoot = styled.div`
  border: 1px solid var(--border-subtle);
  background: var(--bg-panel);
  border-radius: 18px;
  backdrop-filter: blur(14px);
`;

export const Surface = SurfaceRoot;

export const Inline = styled.div<{
  gap?: number;
  align?: string;
  justify?: string;
  $wrap?: boolean;
}>`
  display: flex;
  gap: ${({ gap = 8 }) => `${gap}px`};
  align-items: ${({ align = 'center' }) => align};
  justify-content: ${({ justify = 'flex-start' }) => justify};
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
`;

const buttonAppearanceStyles: Record<ButtonAppearance, ReturnType<typeof css>> = {
  primary: css`
    background: var(--accent);
    color: var(--accent-contrast);
    border-color: transparent;
  `,
  secondary: css`
    background: var(--bg-hover);
    color: var(--text-primary);
    border-color: var(--border-subtle);
  `,
  ghost: css`
    background: transparent;
    color: var(--text-secondary);
    border-color: transparent;
  `,
};

const ButtonRoot = styled.button<{
  $appearance: ButtonAppearance;
  $dimension: ButtonDimension;
}>`
  border-radius: 12px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $dimension }) => ($dimension === 's' ? '8px 12px' : '10px 14px')};
  min-height: ${({ $dimension }) => ($dimension === 's' ? '34px' : '40px')};
  cursor: pointer;
  transition: transform 0.16s ease, background-color 0.16s ease, border-color 0.16s ease, opacity 0.16s ease;
  ${({ $appearance }) => buttonAppearanceStyles[$appearance]}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  appearance?: ButtonAppearance;
  dimension?: ButtonDimension;
  loading?: boolean;
};

export const Button: React.FC<ButtonProps> = ({
  appearance = 'secondary',
  dimension = 'm',
  loading,
  children,
  disabled,
  ...props
}) => (
  <ButtonRoot $appearance={appearance} $dimension={dimension} disabled={disabled || loading} {...props}>
    {loading ? 'Загрузка...' : children}
  </ButtonRoot>
);

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PageHeaderRoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

type PageHeaderProps = {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
};

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <PageHeaderRoot>
    <div>
      <TTypography font="Header/H2">{title}</TTypography>
      {subtitle ? (
        <TTypography font="Body/Body 2 Long" style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
          {subtitle}
        </TTypography>
      ) : null}
    </div>
    {actions ? <Inline gap={8} $wrap>{actions}</Inline> : null}
  </PageHeaderRoot>
);

type TypographyProps = React.HTMLAttributes<HTMLElement> & {
  font?: string;
  as?: keyof React.JSX.IntrinsicElements;
};

export const TTypography: React.FC<TypographyProps> = ({ font = 'Body/Body 2 Long', as, style, children, ...props }) => {
  const Tag: string = as ?? (font.startsWith('Header') ? 'h3' : 'div');
  const fontStyles: React.CSSProperties =
    font === 'Header/H2'
      ? { margin: 0, fontSize: 28, lineHeight: '34px', fontWeight: 700 }
      : font === 'Header/H3'
        ? { margin: 0, fontSize: 22, lineHeight: '28px', fontWeight: 700 }
        : font === 'Header/H4'
          ? { margin: 0, fontSize: 18, lineHeight: '24px', fontWeight: 700 }
          : font === 'Header/H5'
            ? { margin: 0, fontSize: 14, lineHeight: '20px', fontWeight: 700 }
            : font === 'Caption/Caption 1'
              ? { margin: 0, fontSize: 12, lineHeight: '16px', fontWeight: 500, color: 'var(--text-secondary)' }
              : font === 'Body/Body 1 Long'
                ? { margin: 0, fontSize: 15, lineHeight: '22px', fontWeight: 600 }
                : { margin: 0, fontSize: 14, lineHeight: '20px', fontWeight: 500 };

  return React.createElement(Tag, { style: { ...fontStyles, ...style }, ...props }, children);
};

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const inputStyles = css`
  width: 100%;
  min-height: 40px;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-input);
  color: var(--text-primary);
  padding: 0 12px;
  outline: none;
  transition: border-color 0.16s ease, background-color 0.16s ease;

  &:focus {
    border-color: var(--accent-border);
    background: var(--bg-panel-elevated);
  }
`;

const InputRoot = styled.input`
  ${inputStyles}
`;

const SelectRoot = styled.select`
  ${inputStyles}
`;

type InputFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
};

export const InputField: React.FC<InputFieldProps> = ({ label, ...props }) => (
  <FieldLabel>
    {label ? <TTypography font="Caption/Caption 1">{label}</TTypography> : null}
    <InputRoot {...props} />
  </FieldLabel>
);

type SelectFieldProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  children: React.ReactNode;
};

export const SelectField: React.FC<SelectFieldProps> = ({ label, children, ...props }) => (
  <FieldLabel>
    {label ? <TTypography font="Caption/Caption 1">{label}</TTypography> : null}
    <SelectRoot {...props}>{children}</SelectRoot>
  </FieldLabel>
);

const badgeToneStyles: Record<BadgeTone, React.CSSProperties> = {
  neutral: { background: 'var(--badge-neutral-bg)', color: 'var(--text-secondary)', borderColor: 'var(--border-subtle)' },
  primary: { background: 'var(--badge-primary-bg)', color: 'var(--text-primary)', borderColor: 'var(--accent-border)' },
  warning: { background: 'var(--badge-warning-bg)', color: 'var(--text-primary)', borderColor: 'rgba(255, 184, 77, 0.35)' },
  success: { background: 'rgba(52, 211, 153, 0.12)', color: '#34d399', borderColor: 'rgba(52, 211, 153, 0.3)' },
  danger: { background: 'rgba(229, 72, 77, 0.1)', color: '#e5484d', borderColor: 'rgba(229, 72, 77, 0.25)' },
};

type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone;
};

export const Badge: React.FC<BadgeProps> = ({ tone = 'neutral', style, children, ...props }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 24,
      padding: '0 9px',
      borderRadius: 999,
      border: '1px solid',
      fontSize: 12,
      fontWeight: 600,
      ...badgeToneStyles[tone],
      ...style,
    }}
    {...props}
  >
    {children}
  </span>
);

type EmptyStateProps = {
  title: string;
  description?: string;
  action?: React.ReactNode;
};

export const EmptyState: React.FC<EmptyStateProps> = ({ title, description, action }) => (
  <Surface style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
    <TTypography font="Header/H3">{title}</TTypography>
    {description ? <TTypography font="Body/Body 2 Long">{description}</TTypography> : null}
    {action}
  </Surface>
);

const ModalBackdropRoot = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(6, 8, 14, 0.6);
  backdrop-filter: blur(10px);
  z-index: 1000;
`;

export const ModalBackdrop = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function ModalBackdrop(props, ref) {
    React.useEffect(() => {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }, []);
    return <ModalBackdropRoot ref={ref} {...props} />;
  }
);

export const ModalCard = styled.div`
  width: min(480px, 100%);
  max-height: calc(100vh - 48px);
  padding: 20px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
  overflow-y: auto;
  overscroll-behavior: contain;
`;
